import { baseAction } from '../../../actions/baseActions';
import { editAction } from '../../../actions/editActions';
import { AssertionException } from '../../../utils/log';
import { Guid } from '../../model/guid';
import {
  createItemParentId,
  createParentId,
  Hotspot,
  isHotspot,
  isMenuItem,
  isNonSequencedFlowItem,
  isQuiz,
  isResourcesFolder,
  isSection,
  isSequencedFlowItem,
  isStartingMessage,
  Item,
  ItemHelper,
  ItemType,
  MenuItem,
  NonSequencedFlowItem,
  Quiz,
  ResourcesFolder,
  Section,
  SequencedFlowItem,
} from '@ael/core-api';
import { List, OrderedMap } from 'immutable';

export type ItemsReducerState = OrderedMap<string, Item>;

function updateItem(state: ItemsReducerState, item: Item) {
  return state.set(item.id, item);
}

const findIndexOfFirstSection = (storeItems: ItemsReducerState, items: List<string>) => {
  return items
    .map((i) => (i ? storeItems.get(i)?.type : null))
    .toList()
    .findIndex((type) => (type ? type === ItemType.Section : false));
};

const insertContentItem = (storeItems: ItemsReducerState, items: List<string>, item: Item) => {
  const indexPosition = findIndexOfFirstSection(storeItems, items);
  if (indexPosition >= 0 && !isSection(item)) {
    return items.insert(indexPosition, item.id);
  } else {
    return items.push(item.id);
  }
};

const removeAllChildItems = (storeItems: ItemsReducerState, item: Item) => {
  let tempState = storeItems;

  if (isMenuItem(item)) {
    for (let index = 0; index < item.childItems.size; index++) {
      const menuItemChildKey = item.childItems.get(index);
      if (!menuItemChildKey) {
        throw AssertionException('menuItemChildKey cannot be undefined.');
      }
      const menuItemChild = tempState.get(menuItemChildKey);

      if (!menuItemChild) {
        throw AssertionException('menuItemChild cannot be undefined.');
      }
      tempState = removeAllChildItems(tempState, menuItemChild);
      tempState = tempState.remove(menuItemChildKey);
    }
  }

  if (isQuiz(item)) {
    for (let index = 0; index < item.contentItems.size; index++) {
      const contentItem = item.contentItems.get(index);
      if (!contentItem) {
        throw AssertionException('contentItem cannot be undefined.');
      }
      tempState = tempState.remove(contentItem);
    }
  }

  if (isNonSequencedFlowItem(item)) {
    for (let index = 0; index < item.hotspots.size; index++) {
      const contentItem = item.hotspots.get(index);
      if (!contentItem) {
        throw AssertionException('contentItem cannot be undefined.');
      }
      tempState = tempState.remove(contentItem);
    }

    if (item.startingMessageId) {
      tempState = tempState.remove(item.startingMessageId);
    }
  }

  if (isSequencedFlowItem(item)) {
    for (let index = 0; index < item.contentItems.size; index++) {
      const flowContentItemId = item.contentItems.get(index);
      if (!flowContentItemId) {
        throw AssertionException('flowContentItemId cannot be undefined');
      }
      const contentItem = tempState.get(flowContentItemId);

      if (contentItem && isSection(contentItem)) {
        tempState = removeAllChildItems(tempState, contentItem);
      }
      tempState = tempState.remove(flowContentItemId);
    }
  }

  if (isSection(item)) {
    for (let index = 0; index < item.children.size; index++) {
      const childrenKey = item.children.get(index);
      if (!childrenKey) {
        throw AssertionException('childrenKey cannot be undefined.');
      }
      const children = tempState.get(childrenKey);

      if (children && isSection(children)) {
        tempState = removeAllChildItems(tempState, children);
      }
      tempState = tempState.remove(childrenKey);
    }
  }
  if (isResourcesFolder(item)) {
    for (let index = 0; index < item.resources.size; index++) {
      const childrenKey = item.resources.get(index);
      if (!childrenKey) {
        throw AssertionException('childrenKey cannot be undefined.');
      }
      tempState = tempState.remove(childrenKey);
    }
  }

  return tempState.remove(item.id);
};

function removeItem(state: ItemsReducerState, id: string) {
  return state.remove(id);
}

function removeItemFromParentMenuItem(state: ItemsReducerState, parentMenuItem: MenuItem, id: string) {
  const itemIndex = parentMenuItem.childItems.findIndex((itemId) => itemId === id);
  return updateItem(state, {
    ...parentMenuItem,
    childItems: itemIndex >= 0 ? parentMenuItem.childItems.remove(itemIndex) : parentMenuItem.childItems,
  } as MenuItem);
}

function removeItemFromPosition(items: List<Guid>, position: number) {
  return items.remove(position);
}

function insertItemToPosition(items: List<Guid>, itemId: Guid, position: number) {
  return items.insert(position, itemId);
}

function changeItemPosition({
  items,
  oldPosition,
  newPosition,
  itemId,
}: {
  items: List<Guid>;
  oldPosition: number;
  newPosition: number;
  itemId: Guid;
}): List<Guid> {
  return insertItemToPosition(removeItemFromPosition(items, oldPosition), itemId, newPosition);
}

type ChangeItemPositionMethod<T extends Item> = ({
  parentItem,
  oldPosition,
  newPosition,
  state,
  movedItemId,
}: {
  parentItem: T;
  movedItemId: Guid;
  oldPosition: number;
  newPosition: number;
  state: ItemsReducerState;
}) => ItemsReducerState;

const createChangeItemPosition =
  <T extends Item>(
    getChildItems: (item: T) => List<Guid>,
    updateChildItems: (item: T, updatedChildItem: List<Guid>) => T
  ): ChangeItemPositionMethod<T> =>
  ({
    parentItem,
    oldPosition,
    newPosition,
    state,
    movedItemId,
  }: {
    parentItem: T;
    movedItemId: Guid;
    oldPosition: number;
    newPosition: number;
    state: ItemsReducerState;
  }): ItemsReducerState => {
    const childItems = getChildItems(parentItem);
    const reorderedChildItems = changeItemPosition({
      items: childItems,
      oldPosition,
      newPosition,
      itemId: movedItemId,
    });
    const updatedParentItem = updateChildItems(parentItem, reorderedChildItems);
    const updatedState = updateItem(state, updatedParentItem);

    return updatedState;
  };

const changeQuizContentItemPosition = createChangeItemPosition(
  (quiz: Quiz) => quiz.contentItems,
  (quiz, updatedQuizContentItems) => ({
    ...quiz,
    contentItems: updatedQuizContentItems,
  })
);

const changeHotspotPosition = createChangeItemPosition(
  (nonSequencedFlowItem: NonSequencedFlowItem) => nonSequencedFlowItem.hotspots,
  (nonSequencedFlowItem, updatedHotspots): NonSequencedFlowItem => ({
    ...nonSequencedFlowItem,
    hotspots: updatedHotspots,
  })
);

const changeTabPosition = createChangeItemPosition(
  (hotspot: Hotspot) => hotspot.tabs,
  (hotspot, updatedTabs): Hotspot => ({
    ...hotspot,
    tabs: updatedTabs,
  })
);

const changeResourcePosition = createChangeItemPosition(
  (resourcesFolder: ResourcesFolder) => resourcesFolder.resources,
  (resourcesFolder, updatedResources): ResourcesFolder => ({
    ...resourcesFolder,
    resources: updatedResources,
  })
);

function getChangePositionMethodForParent(parentItem: Item): ChangeItemPositionMethod<any> {
  // TODO fix this - should be "Item" instead of any
  if (isQuiz(parentItem)) {
    return changeQuizContentItemPosition;
  }
  if (isNonSequencedFlowItem(parentItem)) {
    return changeHotspotPosition;
  }
  if (isHotspot(parentItem)) {
    return changeTabPosition;
  }
  if (isResourcesFolder(parentItem)) {
    return changeResourcePosition;
  }

  throw AssertionException(`Unknown item type ${parentItem.type}`);
}

function reorderContentItem(
  parentItem: Item,
  state: ItemsReducerState,
  itemId: Guid,
  oldPosition: number,
  newPosition: number
) {
  const changePositionMethod = getChangePositionMethodForParent(parentItem);

  return changePositionMethod({ parentItem, oldPosition, newPosition, state, movedItemId: itemId });
}

function move({
  itemId,
  oldParentItem,
  newParentItem,
  state,
  oldPosition,
  newPosition,
}: {
  itemId: Guid;
  oldParentItem: Item | null;
  newParentItem: Item | null;
  state: OrderedMap<Guid, Item>;
  oldPosition: number;
  newPosition: number;
}) {
  const movingToMenuItem = newParentItem === null || isMenuItem(newParentItem);
  const movingFromMenuItem = oldParentItem === null || isMenuItem(oldParentItem);
  const movingItem = movingFromMenuItem && movingToMenuItem;
  if (movingItem) {
    return moveItem({
      childItemId: itemId,
      oldParentItem,
      newParentItem,
      state,
      oldPosition,
      newPosition,
    });
  }

  if (newParentItem === null || oldParentItem === null) {
    throw AssertionException('Unexpected, unless moving items old and new parent item has to be filled');
  }

  return moveContentItem({ itemId, oldParentItem, newParentItem, newPosition, oldPosition, state });
}

function moveItem({
  childItemId,
  oldParentItem,
  newParentItem,
  state,
  oldPosition,
  newPosition,
}: {
  childItemId: Guid;
  oldParentItem: MenuItem | null;
  newParentItem: MenuItem | null;
  state: OrderedMap<Guid, Item>;
  oldPosition: number;
  newPosition: number;
}) {
  if (oldParentItem) {
    state = updateItem(state, {
      ...oldParentItem,
      childItems: removeItemFromPosition(oldParentItem.childItems, oldPosition),
    } as MenuItem);
  }

  if (newParentItem) {
    // Refresh of values is required as we could've just changed the parent in previous removal
    const currentNewParentItem = state.get(newParentItem.id) as MenuItem;
    state = updateItem(state, {
      ...currentNewParentItem,
      childItems: insertItemToPosition(currentNewParentItem.childItems, childItemId, newPosition),
    } as MenuItem);
  }

  const childItem = state.get(childItemId);
  if (!childItem) {
    throw AssertionException('childItem cannot be undefined');
  }
  const itemWithCorrectParent = ItemHelper.updateParentId(childItem, createItemParentId(newParentItem?.id));

  return updateItem(state, itemWithCorrectParent);
}

function moveSequencedFlowContentItem({
  oldParentItem,
  state,
  oldPosition,
  newParentItem,
  newPosition,
  itemId,
}: {
  oldParentItem: SequencedFlowItem | Section;
  state: OrderedMap<string, Item>;
  oldPosition: number;
  newParentItem: Item | SequencedFlowItem | Section;
  newPosition: number;
  itemId: string;
}) {
  if (isSequencedFlowItem(oldParentItem)) {
    state = updateItem(state, {
      ...oldParentItem,
      contentItems: oldParentItem.contentItems.remove(oldPosition),
    } as SequencedFlowItem);
  }
  if (isSection(oldParentItem)) {
    state = updateItem(state, {
      ...oldParentItem,
      children: oldParentItem.children.remove(oldPosition),
    } as Section);
  }
  // Refresh of values is required as we might've just changed the parent in previous removal
  const currentNewParentItem = state.get(newParentItem.id);
  if (currentNewParentItem && isSequencedFlowItem(currentNewParentItem)) {
    state = updateItem(state, {
      ...currentNewParentItem,
      contentItems: currentNewParentItem.contentItems.insert(newPosition, itemId),
    } as SequencedFlowItem);
  }
  if (currentNewParentItem && isSection(currentNewParentItem)) {
    state = updateItem(state, {
      ...currentNewParentItem,
      children: currentNewParentItem.children.insert(newPosition, itemId),
    } as Section);
  }

  const childItem = state.get(itemId);
  if (!childItem) {
    throw AssertionException('childItem cannot be undefined');
  }
  const updatedChildItem = ItemHelper.updateParentId(childItem, createParentId(newParentItem.id));

  return updateItem(state, updatedChildItem);
}

function moveContentItem({
  itemId,
  oldParentItem,
  state,
  oldPosition,
  newParentItem,
  newPosition,
}: {
  itemId: Guid;
  oldParentItem: Item;
  state: ItemsReducerState;
  oldPosition: number;
  newParentItem: Item;
  newPosition: number;
}) {
  if (isSequencedFlowItem(oldParentItem) || isSection(oldParentItem)) {
    return moveSequencedFlowContentItem({
      itemId,
      oldParentItem,
      newParentItem,
      newPosition,
      oldPosition,
      state,
    });
  }

  // Reorder, in this branch we're definitely not changing parent.
  return reorderContentItem(oldParentItem, state, itemId, oldPosition, newPosition);
}

export function createItemsReducer(startingState: OrderedMap<string, Item>) {
  return (state: ItemsReducerState = startingState, action: any): ItemsReducerState => {
    switch (action.type) {
      case baseAction.ContentItems_Loaded:
        return state.concat(action.payload.contentItems) as ItemsReducerState;
      case editAction.EditAction_UpdateItem:
        return updateItem(state, action.payload);
      case editAction.RightMenu_SetItemProperty:
        return updateItem(state, { ...state.get(action.payload.item.id), ...action.payload.data });
      case editAction.EditAction_CreateNewContentItem: {
        const parentItem: Item | undefined = state.get(action.payload.parentItemId);
        if (!parentItem) {
          return state;
        }

        if (isSequencedFlowItem(parentItem)) {
          const updatedParent: SequencedFlowItem = {
            ...parentItem,
            contentItems: insertContentItem(state, parentItem.contentItems, action.payload.item),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isNonSequencedFlowItem(parentItem)) {
          if (isStartingMessage(action.payload.item)) {
            const item: NonSequencedFlowItem = {
              ...parentItem,
              startingMessageId: action.payload.item.id,
            };
            const tempState = updateItem(state, item);

            return updateItem(tempState, action.payload.item);
          }
          const updatedParent: NonSequencedFlowItem = {
            ...parentItem,
            hotspots: parentItem.hotspots.push(action.payload.item.id),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isSection(parentItem)) {
          const updatedParent: Section = {
            ...parentItem,
            children: insertContentItem(state, parentItem.children, action.payload.item),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isQuiz(parentItem)) {
          const updatedParent: Quiz = {
            ...parentItem,
            contentItems: parentItem.contentItems.push(action.payload.item.id),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isHotspot(parentItem)) {
          const updatedParent: Hotspot = {
            ...parentItem,
            tabs: parentItem.tabs.push(action.payload.item.id),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isResourcesFolder(parentItem)) {
          const updatedParent: ResourcesFolder = {
            ...parentItem,
            resources: parentItem.resources.push(action.payload.item.id),
          };
          const tempState = updateItem(state, updatedParent);

          return updateItem(tempState, action.payload.item);
        }

        if (isMenuItem(parentItem)) {
          if (isStartingMessage(action.payload.item)) {
            const updatedParent: MenuItem = {
              ...parentItem,
              startingMessageId: action.payload.item.id,
            };
            const tempState = updateItem(state, updatedParent);

            return updateItem(tempState, action.payload.item);
          }
        }
        return state;
      }
      case editAction.EditAction_CreateNewItem:
      case editAction.EditAction_CreateResourcesFolder: {
        if (action.payload.parentItemId) {
          const parentItem = state.get(action.payload.parentItemId) as MenuItem;
          if (parentItem.childItems) {
            const tempState = updateItem(state, {
              ...parentItem,
              childItems: parentItem.childItems.push(action.payload.item.id),
            } as MenuItem);

            return updateItem(tempState, action.payload.item);
          }
        }
        return updateItem(state, action.payload.item);
      }

      case editAction.EditAction_CopyItemUnderMenu: {
        if (action.payload.parentItemId && action.payload.itemId) {
          const parentItem = state.get(action.payload.parentItemId) as MenuItem;
          if (parentItem.childItems) {
            return updateItem(state, {
              ...parentItem,
              childItems: parentItem.childItems.push(action.payload.itemId),
            } as MenuItem);
          }
        }
        return state;
      }

      case editAction.EditAction_Reorder: {
        const oldPosition = action.payload.oldPosition as number;
        const newPosition = action.payload.newPosition as number;
        const itemId = action.payload.itemId as string;

        const oldParentItem: Item | null = action.payload.oldParentItemId
          ? state.get(action.payload.oldParentItemId) || null
          : null;
        const newParentItem: Item | null = action.payload.newParentItemId
          ? state.get(action.payload.newParentItemId) || null
          : null;

        return move({
          itemId,
          oldParentItem,
          newParentItem,
          state,
          oldPosition,
          newPosition,
        });
      }

      case editAction.EditAction_DeleteItem: {
        const item = action.payload.item as Item;
        const id = item.id;
        const parentItemId = action.payload.parentItemId;
        const parentMenuItem = parentItemId ? (state.get(action.payload.parentItemId) as MenuItem) : null;

        let tempState = removeItem(state, id);
        if (parentMenuItem) {
          tempState = removeItemFromParentMenuItem(tempState, parentMenuItem, id);
        }

        return removeAllChildItems(tempState, item);
      }

      case editAction.EditAction_DeleteContentItem: {
        const parentItemId = action.payload.item.parentId.parentId;
        if (!parentItemId) {
          return state;
        }

        const parentItem = state.get(parentItemId);
        if (!parentItem) {
          return state;
        }

        if (isQuiz(parentItem)) {
          const itemIndex = parentItem.contentItems.findIndex((id) => id === action.payload.item.id);

          const stateWithRemovedQuiz = state.remove(action.payload.item.id);
          return stateWithRemovedQuiz.set(parentItem.id, {
            ...parentItem,
            contentItems: parentItem.contentItems.remove(itemIndex),
          } as Quiz);
        }

        if (isNonSequencedFlowItem(parentItem)) {
          if (isStartingMessage(action.payload.item)) {
            const stateWithRemovedStartingMessage = state.remove(action.payload.item.id);
            return stateWithRemovedStartingMessage.set(parentItem.id, {
              ...parentItem,
              startingMessageId: undefined,
            } as NonSequencedFlowItem);
          }

          const itemIndex = parentItem.hotspots.findIndex((hotspotId) => hotspotId === action.payload.item.id);
          const stateWithRemovedHotspot = state.remove(action.payload.item.id);
          return stateWithRemovedHotspot.set(parentItem.id, {
            ...parentItem,
            hotspots: parentItem.hotspots.remove(itemIndex),
          } as NonSequencedFlowItem);
        }

        if (isHotspot(parentItem)) {
          const itemIndex = parentItem.tabs.findIndex((tabId) => tabId === action.payload.item.id);

          const tempState = state.remove(action.payload.item.id);
          return updateItem(tempState, {
            ...parentItem,
            tabs: parentItem.tabs.remove(itemIndex),
          } as Hotspot);
        }

        if (isMenuItem(parentItem)) {
          if (isStartingMessage(action.payload.item)) {
            const tempState = state.remove(action.payload.item.id);
            return updateItem(tempState, {
              ...parentItem,
              startingMessageId: undefined,
            } as MenuItem);
          }
        }

        if (isSequencedFlowItem(parentItem)) {
          const itemIndex = parentItem.contentItems.findIndex((itemId) => itemId === action.payload.item.id);

          const tempState = state.remove(action.payload.item.id);
          return updateItem(tempState, {
            ...parentItem,
            contentItems: parentItem.contentItems.remove(itemIndex),
          } as SequencedFlowItem);
        }

        if (isSection(parentItem)) {
          const itemIndex = parentItem.children.findIndex((itemId) => itemId === action.payload.item.id);

          const tempState = state.remove(action.payload.item.id);
          return updateItem(tempState, {
            ...parentItem,
            children: parentItem.children.remove(itemIndex),
          } as Section);
        }
        if (isResourcesFolder(parentItem)) {
          const itemIndex = parentItem.resources.findIndex((itemId) => itemId === action.payload.item.id);

          const tempState = state.remove(action.payload.item.id);
          return updateItem(tempState, {
            ...parentItem,
            resources: parentItem.resources.remove(itemIndex),
          } as ResourcesFolder);
        }
        return state;
      }

      default:
        return state;
    }
  };
}
